import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import biogenFunctionOptions from 'basics/options/biogenFunction.options';
import useProductOptions from 'basics/options/product.options';
import sotrFilterOptions from 'basics/options/sotrFilter.options';
import useTherapeuticAreaOptions from 'basics/options/therapeuticArea.options';
import GridListAction from 'components/GridListAction/GridListAction';
import { ProductsContext } from 'contexts/products';
import { SortFilter } from 'generated/maya.types';

const useCepTemplateListConfig = (
  editCepTemplateCallback: (cepId: string) => void,
  CepFromTemplateCallback: (cepId: string) => void,
  openModalCallback: (cepId: string) => void,
  therapeuticArea: string[],
) => {
  const { t } = useTranslation();
  const flags = useFlags();
  const { allProducts } = useContext(ProductsContext);
  const currentTA = therapeuticArea ?? [''];

  const cepTitleFieldConfig = {
    name: 'name',
    placeholder: t('maya_cep_tpl_title_placeholder'),
    defaultValue: '',
    required: false,
  };

  const cepTherapeuticAreaFieldConfig = {
    name: 'therapeuticArea',
    placeholder: t('maya_cep_tpl_therapeutic_area_placeholder'),
    options: useTherapeuticAreaOptions(allProducts),
    defaultValue: [],
    required: false,
    disableRenderTags: true,
  };

  const cepProductFieldConfig = {
    name: 'product',
    placeholder: t('maya_cep_tpl_product_placeholder'),
    options: useProductOptions(currentTA, allProducts),
    defaultValue: [],
    required: false,
    disableRenderTags: true,
  };

  const cepBiogenFunctionFieldConfig = {
    name: 'cepBiogenFunction',
    placeholder: t('maya_cep_tpl_biogen_function_placeholder'),
    options: biogenFunctionOptions,
    defaultValue: [],
    required: false,
    disableRenderTags: true,
  };

  const cepSortFieldConfig = {
    name: 'sort',
    placeholder: t('sort_placeholder'),
    options: sotrFilterOptions,
    defaultValue: SortFilter.MOST_POPULAR,
    required: false,
    disableRenderTags: true,
  };

  const cepListColumnConfig: GridColDef[] = [
    {
      field: 'title',
      headerName: t('maya_cep_list_title_header_label'),
      flex: 0.3,
      sortable: false,
      cellClassName: 'gridlist-cell-bold',
    },
    {
      field: 'formattedCepId',
      headerName: t('maya_cep_list_cep_id_header_label'),
      flex: 0.2,
      sortable: false,
    },
    {
      field: 'popularity',
      headerName: t('maya_cep_list_popularity_header_label'),
      flex: 0.1,
      sortable: false,
    },
    {
      field: 'action',
      headerName: '',
      disableColumnMenu: true,
      hideSortIcons: true,
      renderCell: (params: GridRenderCellParams) => {
        const actions = [
          {
            label: t('actions_edit_label'),
            callback: () => { editCepTemplateCallback(params.row._id); },
          },
          {
            label: t('actions_create_cep_from_tpl'),
            callback: () => { CepFromTemplateCallback(params.row._id); },
          },
        ];

        if (flags.mayaBiogenlinc3595EnableCepDeleting) {
          actions.push({
            label: t('maya_action_delete_cep_label'),
            callback: () => {
              openModalCallback(params.row._id);
            },
          });
        }

        return (
          <GridListAction
            actions={ actions }
          />
        );
      },
      align: 'right',
      headerAlign: 'right',
      flex: 0.05,
    },
  ];

  return {
    cepListColumnConfig,
    cepTitleFieldConfig,
    cepTherapeuticAreaFieldConfig,
    cepProductFieldConfig,
    cepBiogenFunctionFieldConfig,
    cepSortFieldConfig,
  };
};

export default useCepTemplateListConfig;
